import { v4 as uuidv4 } from 'uuid'
import { attachDriver, Signal } from 'rwwa-rx-state-machine'
import { makeTypedFactory, TypedRecord } from 'typed-immutable-record'
import { ToteRaceStatusChangedPushEvent } from 'tabtouch-push-contract'
import type { KambiValidReward } from '@mobi/api-types/kambi/player/reward'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import type {
  Selection,
  MysterySelection,
  EventDetails,
  BettingType,
  BetError,
  BetPrices,
  BetSlipItem,
  BetSlipErrorResponse,
  BetSource,
  KambiSportsSelection,
} from '@mobi/betslip/types'
import {
  isFobSelection,
  isMysterySelection,
  isRaceDetails,
  isStartingPriceSelection,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import {
  BetResponseCode,
  MysteryProposeResponse,
  BetErrorType,
  toBetErrorType,
} from '@core/Data/betting'
import { isMysteryDetails } from '@core/Data/Betting/selectionDetails'
import {
  ChangeInvestment,
  ProposeBet,
  ProposeBetFailed,
  ProposeBetSucceeded,
  ConfirmBet,
  ConfirmBetFailed,
  ConfirmBetSucceeded,
  ConfirmBetslipBetsSucceeded,
  ConfirmBetslipBetsFailed,
  DepositFundsDisplayed,
  QuickbetLoadSelection,
  SetActiveInvestment,
  ToggleBonusBetUsage,
  ToggleBonusCashUsage,
  ToggleEachWay,
  SetInvalidInvestmentNotification,
  AddingToBetslip,
  RaceClosedEventReceived,
  SetMysteryQuickPickPresetInvestment,
  ProposeBetslipBetsSucceeded,
  QuickbetClosed,
  SetAllowInvestmentState,
  InsufficientFundsForBet,
  ClearInsufficientFundsForBet,
  KeepRaceCardSelections,
  QuickbetLoadMultiSelection,
  ProposeMultiBetSucceeded,
  ConfirmMultiBetSucceeded,
  ConfirmMultiBetFailed,
  ProposeMultiBetFailed,
  MultiBetResponsePriceChange,
  ProposeKambiBetFailed,
  ProposeKambiBetSucceeded,
  ConfirmKambiBetFailed,
  ConfirmKambiBetSucceeded,
  KambiBetResponsePriceChange,
  SelectKambiReward,
  KambiBetResponseRewardNotAvailable,
} from './signals'
import type { QuickbetSelection } from './types'
import { HasLoggedIn } from '@core/State/UserAccount/userAccountDriver'
import {
  ChangeSelectedSuperPick,
  LoadSuperPicks,
  UpdateSelectedSuperPick,
} from './Components/SuperPick/Components/SuperPick/signals'
import { ToggleFormula } from './Components/Formula/driver'
import type { NotificationType } from './Components/Notifications/types'
import { updateKambiSelectionWithNewPrice, shouldShowPriceChangeMessage } from './helpers/state'
import { toMoney } from '@mobi/utils/money'
import { formatKambiCurrencyToDollarDecimal } from '../KambiSports/helpers/currencyFormat'

export interface QuickbetState {
  id: string | null
  bettingType: BettingType | null
  canBet: boolean
  canChangeInvestment: boolean
  betPlaced: boolean
  isBusy: boolean
  canProposeBet: boolean
  canConfirmBet: boolean
  selection: Selection | null
  selectionDetails: EventDetails | null
  selectionMultiLegs: BetSlipItem[] | null
  shouldAllowWinInvestment: boolean
  shouldAllowPlaceInvestment: boolean
  shouldKeepRaceCardSelections: boolean
  isEachWayAvailable: boolean
  isEachWay: boolean
  isUsingBonusBet: boolean
  isUsingBonusCash: boolean
  isAddingToBetslip: boolean
  isQuickbetOpen: boolean
  notificationType: NotificationType | null
  notificationTitle: string | null
  notificationSubtitle: string | null
  presetInvestment: boolean
  promptForInvestment: boolean
  betSource: BetSource | null
  /** Used for bet-loop and share-bet to track origin */
  betSourceShareOrigin: string | null
  /** Used to increment bet loop copy count */
  copiedBetLoopPostId: string | null
  insufficientFundsForBet: boolean
  betCost: number
  shortfall: number
  selectedKambiReward: KambiValidReward | null
}

export const defaultQuickbetState: QuickbetState = {
  id: null,
  bettingType: null,
  canBet: true,
  canChangeInvestment: true,
  betPlaced: false,
  isBusy: false,
  canProposeBet: true,
  canConfirmBet: false,
  selection: null,
  selectionDetails: null,
  selectionMultiLegs: null,
  isEachWayAvailable: false,
  isUsingBonusBet: false,
  isUsingBonusCash: true,
  isEachWay: false,
  shouldAllowWinInvestment: true,
  shouldAllowPlaceInvestment: false,
  shouldKeepRaceCardSelections: false,
  isAddingToBetslip: false,
  presetInvestment: false,
  promptForInvestment: true,
  betSource: null,
  betSourceShareOrigin: null,
  copiedBetLoopPostId: null,
  insufficientFundsForBet: false,
  betCost: 0,
  shortfall: 0,
  isQuickbetOpen: false,
  selectedKambiReward: null,
  ...getDefaultNotificationState(),
}

export interface QuickbetStateRecord extends TypedRecord<QuickbetStateRecord>, QuickbetState {}
export const QuickbetStateFactory = makeTypedFactory<QuickbetState, QuickbetStateRecord>(
  defaultQuickbetState
)

export function quickbetDriver(
  state = QuickbetStateFactory(),
  signal: Signal
): QuickbetStateRecord {
  switch (signal.tag) {
    case QuickbetLoadSelection: {
      const {
        bettingType,
        selection,
        selectionDetails,
        isEachWayAvailable,
        shouldAllowPlaceInvestment,
        presetInvestment = false,
        promptForInvestment = true,
        betSource = null,
        betSourceShareOrigin = null,
        copiedBetLoopPostId = null,
      } = signal.data as QuickbetSelection

      return state.merge({
        ...defaultQuickbetState,
        id: uuidv4(),
        bettingType,
        selection,
        selectionDetails,
        isEachWayAvailable,
        shouldAllowPlaceInvestment,
        presetInvestment,
        promptForInvestment,
        canChangeInvestment: promptForInvestment,
        canProposeBet: promptForInvestment,
        canConfirmBet: !promptForInvestment,
        notificationType: !promptForInvestment ? 'ProposeSucceeded' : null,
        betSource,
        betSourceShareOrigin,
        copiedBetLoopPostId,
        isQuickbetOpen: true,
      })
    }

    case QuickbetLoadMultiSelection: {
      const {
        selectionMultiLegs,
        betSource = null,
        betSourceShareOrigin = null,
        copiedBetLoopPostId = null,
      } = signal.data as Pick<
        QuickbetState,
        | 'selectionMultiLegs'
        | 'betSource'
        | 'betSourceShareOrigin'
        | 'copiedBetLoopPostId'
      >

      return state.merge({
        ...defaultQuickbetState,
        id: uuidv4(),
        bettingType: 'fixed-odds-racing',
        selectionMultiLegs,
        shouldAllowPlaceInvestment: false,
        betSource,
        betSourceShareOrigin,
        copiedBetLoopPostId,
        isQuickbetOpen: true,
      })
    }

    case ToggleFormula:
    case ChangeInvestment: {
      const currentState: QuickbetState = state.toJS()
      const selection = currentState.selection as MysterySelection

      if (
        currentState.bettingType === 'mystery-quick-pick' ||
        currentState.bettingType === 'mystery-custom-bet'
      ) {
        selection.bets = null
        selection.metaData = null
      }

      state = state.merge({
        selection,
        insufficientFundsForBet: false,
        betCost: 0,
        shortfall: 0,
        ...getCanChangeInvestmentState(),
      })

      // Need to force the Notification and canChangeInvestment as canChangeInvestmentState will clear it
      if (currentState.bettingType === 'mystery-quick-pick') {
        state = state.merge({
          canChangeInvestment: false,
          notificationType: 'MysteryQuickPickFixedInvestment',
        })
      }

      return state
    }

    case SetActiveInvestment: {
      return state.merge(getCanChangeInvestmentState())
    }

    case ChangeSelectedSuperPick: {
      const isConfirmationStage = !state.canProposeBet && state.canConfirmBet
      const hasInvalidSuperPickSelection = !state.canChangeInvestment && state.canProposeBet

      return hasInvalidSuperPickSelection || isConfirmationStage
        ? state.merge(getCanChangeInvestmentState())
        : state
    }

    case SetInvalidInvestmentNotification: {
      const { type, title = null, subtitle = null } = signal.data
      return state.merge({
        notificationType: type,
        notificationTitle: title,
        notificationSubtitle: subtitle,
        canChangeInvestment: false,
      })
    }

    case ToggleBonusBetUsage: {
      const forcedValue = signal.data
      const isUsingBonusBet = forcedValue != undefined ? forcedValue : !state.get('isUsingBonusBet')
      const isUsingBonusCash = isUsingBonusBet ? false : state.get('isUsingBonusCash')
      return state.merge({ isUsingBonusBet, isUsingBonusCash })
    }

    case ToggleBonusCashUsage: {
      const value = signal.data
      const isUsingBonusCash = value != undefined ? value : !state.get('isUsingBonusCash')
      const isUsingBonusBet = isUsingBonusCash ? false : state.get('isUsingBonusBet')
      return state.merge({ isUsingBonusCash, isUsingBonusBet })
    }

    case SetAllowInvestmentState: {
      return state.merge({
        shouldAllowPlaceInvestment: signal.data.shouldAllowPlaceInvestment,
        shouldAllowWinInvestment: signal.data.shouldAllowWinInvestment,
      })
    }

    case ToggleEachWay: {
      return state.merge({ ...getCanChangeInvestmentState(), isEachWay: !state.get('isEachWay') })
    }

    case ProposeBet: {
      return state.merge({ isBusy: true })
    }

    case ProposeBetSucceeded:
    case ProposeBetslipBetsSucceeded: {
      const currentState: QuickbetState = { ...state.toJS() }

      if (isMysterySelection(currentState.selection)) {
        const response = signal.data as MysteryProposeResponse
        const bets: MysterySelection['bets'] = response.bets
        const metaData: string = response.metaData
        const selection = currentState.selection
        selection.bets = bets
        selection.metaData = metaData

        return state.merge({
          selection,
          isBusy: false,
          canChangeInvestment: false,
          canProposeBet: false,
          canConfirmBet: true,
          notificationType: 'ProposeSucceeded',
        })
      }

      if (
        isFobSelection(currentState.selection) &&
        !isStartingPriceSelection(currentState.selection)
      ) {
        const currentPrices: BetPrices = {
          winPrice: currentState.selection.winPrice,
          placePrice: currentState.selection.placePrice,
          previousWinPrice: null,
          previousPlacePrice: null,
        }

        const { winPrice: newWinPrice, placePrice: newPlacePrice } = signal.data.prices
        const newPrices: BetPrices = {
          winPrice: newWinPrice === null ? currentPrices.winPrice : newWinPrice,
          placePrice: newPlacePrice === null ? currentPrices.placePrice : newPlacePrice,
          previousWinPrice: null,
          previousPlacePrice: null,
        }

        const specialOffers = signal.data.specialOffers

        if (specialOffers && specialOffers[0]) {
          UpdateSelectedSuperPick({ specialOffers })
        }
        const showPricesChanged = shouldShowPriceChangeMessage(currentPrices, newPrices)
        return state.merge({
          isBusy: false,
          canChangeInvestment: false,
          canProposeBet: false,
          canConfirmBet: true,
          selection: {
            ...currentState.selection,
            winPrice: newPrices.winPrice,
            placePrice: newPrices.placePrice,
          },
          ...getDefaultNotificationState(),
          notificationType: showPricesChanged ? 'PriceChange' : 'ProposeSucceeded',
        })
      }
      return state.merge({
        isBusy: false,
        canChangeInvestment: false,
        canProposeBet: false,
        canConfirmBet: true,
        notificationType: 'ProposeSucceeded',
      })
    }

    case ProposeMultiBetSucceeded: {
      const legs = signal.data as BetSlipItem[]
      return state.merge({
        isBusy: false,
        canChangeInvestment: false,
        canProposeBet: false,
        canConfirmBet: true,
        notificationType: 'ProposeSucceeded',
        selectionMultiLegs: legs,
      })
    }

    case ProposeBetFailed: {
      if (signal.data.code === BetResponseCode.Unauthorized) {
        return state.merge(getUnAuthorizedState())
      }
      if (signal.data.code === BetResponseCode.HandicapChanged) {
        return state.merge({
          isBusy: false,
          canBet: false,
          notificationType: 'HandicapChanged',
          canChangeInvestment: false,
          canProposeBet: false,
          canConfirmBet: false,
        })
      }
      // Fallback case for errors like "Max stake exceeded", "event or market closed".
      return state.merge({
        isBusy: false,
        canConfirmBet: false,
        canChangeInvestment: false,
        notificationType: 'NonHandledError',
        notificationTitle: signal.data.response.message,
        canBet: false,
        canProposeBet: false,
      })
    }

    case ProposeMultiBetFailed: {
      const errorResponse: BetSlipErrorResponse = signal.data
      if (errorResponse.code === BetResponseCode.Unauthorized) {
        return state.merge(getUnAuthorizedState())
      }
      const apiErrorMessage =
        errorResponse.response && errorResponse.response.message
          ? errorResponse.response.message
          : 'Unable to place any bets. Please try again later'
      return state.merge({
        isBusy: false,
        canConfirmBet: false,
        canChangeInvestment: false,
        notificationType: 'NonHandledError',
        notificationTitle: apiErrorMessage,
        canBet: false,
        canProposeBet: false,
      })
    }

    case MultiBetResponsePriceChange: {
      const legs = signal.data as BetSlipItem[]
      return state.merge({
        ...getDefaultNotificationState(),
        notificationType: 'PriceChange',
        isBusy: false,
        canChangeInvestment: false,
        canProposeBet: false,
        canConfirmBet: true,
        selectionMultiLegs: legs,
      })
    }

    // =========================
    // #region Kambi - Place Bet
    // =========================

    case ProposeKambiBetSucceeded: {
      return state.merge({
        isBusy: false,
        canChangeInvestment: false,
        canProposeBet: false,
        canConfirmBet: true,
        notificationType: 'ProposeSucceeded',
      })
    }

    case SelectKambiReward: {
      const data = signal.data as Parameters<typeof SelectKambiReward>[0]
      const currentState: QuickbetState = { ...state.toJS() }
      if (currentState.selectedKambiReward?.rewardId === data?.rewardId) {
        return state.merge({ selectedKambiReward: null })
      }
      return state.merge({ selectedKambiReward: data })
    }

    case ProposeKambiBetFailed: {
      const errorType = signal.data as Parameters<typeof ProposeKambiBetFailed>[0]

      if (errorType === 'Betting Closed') {
        return state.merge(getBettingClosedState())
      }
      if (errorType === 'Mobi Not Logged In') {
        return state.merge(getUnAuthorizedState())
      }
      if (errorType === 'Payout Too High') {
        return state.merge(getPayoutTooHighState())
      }

      if (typeof errorType === 'object' && errorType?.type === 'Stake Too High') {
        return state.merge(getStakeTooHighState(errorType.limit))
      }

      const apiErrorMessage = 'Unable to place any bets. Please try again later'
      return state.merge({
        isBusy: false,
        canConfirmBet: false,
        canChangeInvestment: false,
        notificationType: 'NonHandledError',
        notificationTitle: apiErrorMessage,
        canBet: false,
        canProposeBet: false,
      })
    }

    case ConfirmKambiBetFailed: {
      const errorType = signal.data as Parameters<typeof ConfirmKambiBetFailed>[0]

      if (errorType === 'Betting Closed') {
        return state.merge(getBettingClosedState())
      }
      if (errorType === 'Mobi Not Logged In') {
        return state.merge(getUnAuthorizedState())
      }
      if (errorType === 'Insufficient Funds') {
        return state.merge(getInsufficientFundsState())
      }

      if (errorType === 'Payout Too High') {
        return state.merge(getPayoutTooHighState())
      }

      if (typeof errorType === 'object' && errorType?.type === 'Stake Too High') {
        return state.merge(getStakeTooHighState(errorType.limit))
      }

      const apiErrorMessage = 'Unable to place bet(s) - Please check pending bets and try again'
      return state.merge({
        isBusy: false,
        notificationType: 'NonHandledError',
        notificationTitle: apiErrorMessage,
        canConfirmBet: false,
        canBet: false,
      })
    }

    case KambiBetResponsePriceChange: {
      const data = signal.data as NonNullable<Parameters<typeof KambiBetResponsePriceChange>[0]>
      const currentState: QuickbetState = state.toJS()
      const selection = updateKambiSelectionWithNewPrice(
        currentState.selection as KambiSportsSelection,
        data
      )

      return state.merge({
        ...getDefaultNotificationState(),
        notificationType: 'PriceChange',
        isBusy: false,
        canChangeInvestment: false,
        canProposeBet: false,
        canConfirmBet: true,
        selection,
      })
    }

    case KambiBetResponseRewardNotAvailable: {
      return state.merge({
        ...getDefaultNotificationState(),
        notificationType: 'RewardNotAvailable',
        isBusy: false,
        canChangeInvestment: true,
        canProposeBet: true,
        canConfirmBet: false,
      })
    }

    // #endregion
    // ============================

    case InsufficientFundsForBet: {
      const { betCost, shortfall } = signal.data
      return state.merge({ insufficientFundsForBet: true, betCost, shortfall })
    }

    case ClearInsufficientFundsForBet: {
      return state.merge({ insufficientFundsForBet: false, betCost: 0, shortfall: 0 })
    }

    case ConfirmBet: {
      return state.merge({ isBusy: true })
    }

    case ConfirmBetSucceeded:
    case ConfirmKambiBetSucceeded:
    case ConfirmMultiBetSucceeded:
    case ConfirmBetslipBetsSucceeded: {
      return state.merge({
        isBusy: false,
        canBet: false,
        canConfirmBet: false,
        betPlaced: true,
        ...getDefaultNotificationState(),
      })
    }

    case ConfirmBetslipBetsFailed: {
      state = state.set('betPlaced', false)
      const betslipError = signal.data as BetError[]

      if (
        betslipError.every(error => toBetErrorType(error.type) === BetErrorType.InsufficientFunds)
      ) {
        return state.merge(getInsufficientFundsState())
      }

      if (betslipError.every(error => toBetErrorType(error.type) === BetErrorType.Closed)) {
        return state.merge(getBettingClosedState())
      }

      return state.merge({
        isBusy: false,
        notificationType: 'NonHandledError',
        notificationTitle: 'Unable to place any bets. Please try again later',
        canConfirmBet: false,
        canBet: false,
      })
    }

    case ConfirmMultiBetFailed: {
      const errorResponse: BetSlipErrorResponse = signal.data
      if (errorResponse.code === BetResponseCode.Unauthorized) {
        return state.merge(getUnAuthorizedState())
      }
      if (signal.data.code === BetResponseCode.InsufficientFunds) {
        return state.merge(getInsufficientFundsState())
      }

      const apiErrorMessage =
        errorResponse.response && errorResponse.response.message
          ? errorResponse.response.message
          : 'Unable to place bet(s) - Please check pending bets and try again'

      return state.merge({
        isBusy: false,
        notificationType: 'NonHandledError',
        notificationTitle: apiErrorMessage,
        canConfirmBet: false,
        canBet: false,
      })
    }

    case ConfirmBetFailed: {
      state = state.set('betPlaced', false)

      if (signal.data.code === BetResponseCode.PriceChanged) {
        const currentState: QuickbetState = state.toJS()
        const { winPrice: newWinPrice, placePrice: newPlacePrice } = signal.data.response.prices

        const currentPrices: BetPrices = {
          winPrice: (currentState.selection as { winPrice: number }).winPrice,
          placePrice: (currentState.selection as { placePrice: number }).placePrice,
          previousWinPrice: null,
          previousPlacePrice: null,
        }

        const { winPrice, placePrice }: BetPrices = {
          winPrice: newWinPrice === null ? currentPrices.winPrice : newWinPrice,
          placePrice: newPlacePrice === null ? currentPrices.placePrice : newPlacePrice,
          previousWinPrice: null,
          previousPlacePrice: null,
        }

        if (currentState.bettingType === 'fixed-odds-racing') LoadSuperPicks()
        const isPriceChange = shouldShowPriceChangeMessage(currentPrices, { winPrice, placePrice })

        return state.merge({
          isBusy: false,
          selection: { ...currentState.selection, winPrice, placePrice },
          ...getDefaultNotificationState(),
          notificationType: isPriceChange ? 'PriceChange' : null,
        })
      }
      if (signal.data.code === BetResponseCode.HandicapChanged) {
        return state.merge({
          isBusy: false,
          canBet: false,
          notificationType: 'HandicapChanged',
          canChangeInvestment: false,
          canConfirmBet: false,
          canProposeBet: false,
        })
      }
      if (signal.data.code === BetResponseCode.InsufficientFunds) {
        return state.merge(getInsufficientFundsState())
      }
      if (signal.data.code === BetResponseCode.Unauthorized) {
        return state.merge(getUnAuthorizedState())
      }
      if (signal.data.code === BetResponseCode.NetworkError) {
        return state.merge({
          isBusy: false,
          canBet: false,
          canChangeInvestment: false,
          canConfirmBet: false,
          canProposeBet: false,
          notificationType: 'NonHandledError',
          notificationTitle: signal.data.response.message,
        })
      }
      // Fallback case for errors like "Max stake exceeded", "event or market closed".
      return state.merge({
        isBusy: false,
        notificationType: 'NonHandledError',
        notificationTitle: signal.data.response.message,
        canConfirmBet: false,
        canBet: false,
      })
    }

    case DepositFundsDisplayed: {
      return state.merge({
        isBusy: false,
        canProposeBet: false,
        canConfirmBet: true,
        ...getDefaultNotificationState(),
        notificationType: 'ProposeSucceeded',
      })
    }

    case HasLoggedIn: {
      const { canProposeBet, canConfirmBet, bettingType }: QuickbetState = state.toJS()
      if (canProposeBet && !canConfirmBet) {
        if (bettingType === 'mystery-quick-pick') {
          // Don't do anything for Mystery Quick Pick
          return state
        }
        return state.merge({ ...getDefaultNotificationState(), canChangeInvestment: true })
      }
      if (canConfirmBet && !canProposeBet) {
        return state.merge({
          ...getDefaultNotificationState(),
          notificationType: 'ProposeSucceeded',
        })
      }
      return state
    }

    case AddingToBetslip: {
      return state.merge({ isAddingToBetslip: true })
    }

    case KeepRaceCardSelections: {
      return state.merge({ shouldKeepRaceCardSelections: true })
    }

    case QuickbetClosed: {
      return state.merge({ betSource: null, isQuickbetOpen: false })
    }

    case RaceClosedEventReceived: {
      const { isQuickbetOpen, isBusy, betPlaced, selection, selectionDetails }: QuickbetState =
        state.toJS()
      if (!isQuickbetOpen) {
        const id = signal.data.toastId
        addToast({ id, title: 'Betting Closed', icon: 'error', isDismissable: true, theme: 'dark' })
      }
      const pushEventData = signal.data.event as ToteRaceStatusChangedPushEvent
      if (
        isQuickbetOpen &&
        !isBusy &&
        !betPlaced &&
        (isToteSelection(selection) || isMysterySelection(selection)) &&
        pushEventData.raceId.toString() === getEventId(selectionDetails)
      ) {
        return state.merge(getBettingClosedState())
      }
      return state
    }

    case SetMysteryQuickPickPresetInvestment: {
      return state.merge({
        canChangeInvestment: false,
        notificationType: 'MysteryQuickPickFixedInvestment',
      })
    }

    default: {
      return state
    }
  }
}

export const state$ = attachDriver<QuickbetStateRecord>({
  path: 'quickbet',
  driver: quickbetDriver,
})

// =============
// Local Helpers
// =============

function getEventId(selectionDetails: EventDetails | null) {
  if (isRaceDetails(selectionDetails)) return selectionDetails.races[0].key
  if (isMysteryDetails(selectionDetails)) return selectionDetails.race.key

  return null
}

function getDefaultNotificationState() {
  const state: Pick<
    QuickbetState,
    'notificationType' | 'notificationTitle' | 'notificationSubtitle'
  > = {
    notificationType: null,
    notificationTitle: null,
    notificationSubtitle: null,
  }
  return state
}

function getCanChangeInvestmentState() {
  const state: Partial<QuickbetState> = {
    canChangeInvestment: true,
    canProposeBet: true,
    canConfirmBet: false,
    ...getDefaultNotificationState(),
  }
  return state
}

function getBettingClosedState() {
  const state: Partial<QuickbetState> = {
    isBusy: false,
    canConfirmBet: false,
    canChangeInvestment: false,
    notificationType: 'BettingClosed',
    canBet: false,
    canProposeBet: false,
  }
  return state
}

function getInsufficientFundsState() {
  const state: Partial<QuickbetState> = {
    isBusy: false,
    canConfirmBet: false,
    notificationType: 'InsufficientFunds',
  }
  return state
}

function getUnAuthorizedState() {
  const state: Partial<QuickbetState> = {
    notificationType: 'Unauthorized',
    isBusy: false,
    canChangeInvestment: false,
  }
  return state
}

function getPayoutTooHighState() {
  const state: Partial<QuickbetState> = {
    isBusy: false,
    notificationType: 'NonHandledError' as const,
    notificationTitle: 'Payout exceeds the maximum allowed. Try lowering your stake.',
    canChangeInvestment: true,
    canConfirmBet: false,
    canProposeBet: true,
  }
  return state
}

function getStakeTooHighState(limit?: number) {
  const state: Partial<QuickbetState> = {
    isBusy: false,
    canConfirmBet: false,
    canChangeInvestment: false,
    notificationType: 'NonHandledError' as const,
    notificationTitle: limit
      ? `Your max stake for this bet is ${toMoney(formatKambiCurrencyToDollarDecimal(limit))}`
      : 'Your stake is too high',
    canBet: false,
    canProposeBet: false,
  }
  return state
}
